import { useEffect, useState, type FormEvent } from "react";
import {
  CheckCircle2,
  ChevronRight,
  Pencil,
  Plus,
  RotateCcw,
  Trash2,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { DatePicker } from "@/components/ui/date-picker";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/hooks/useAuth";
import { formatDate } from "@/lib/date-utils";
import { cn } from "@/lib/utils";
import {
  isoDate,
  useDeleteVehicleDamage,
  useSaveVehicleDamage,
  useSetVehicleDamageStatus,
  type VehicleDamage,
  type VehicleDamageSeverity,
} from "@/hooks/useVehicles";

export const damageSeverityLabels: Record<VehicleDamageSeverity, string> = {
  minor: "Minor",
  major: "Major",
  critical: "Not roadworthy",
};

export const damageSeverityClasses: Record<VehicleDamageSeverity, string> = {
  minor: "bg-muted text-foreground",
  major: "bg-amber-100 text-amber-900 dark:bg-amber-900 dark:text-amber-100",
  critical: "bg-red-100 text-red-900 dark:bg-red-900 dark:text-red-100",
};

function DamageDialog({
  open,
  onOpenChange,
  vehicleId,
  damage,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  vehicleId: string;
  damage: VehicleDamage | null;
}) {
  const { personId } = useAuth();
  const save = useSaveVehicleDamage();
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [severity, setSeverity] = useState<VehicleDamageSeverity>("minor");
  const [reportedOn, setReportedOn] = useState("");
  const [imageFile, setImageFile] = useState<File | null>(null);

  useEffect(() => {
    if (!open) return;
    setTitle(damage?.title ?? "");
    setDescription(damage?.description ?? "");
    setSeverity(damage?.severity ?? "minor");
    setReportedOn(
      isoDate(damage?.reportedOn) ?? new Date().toISOString().slice(0, 10),
    );
    setImageFile(null);
  }, [open, damage]);

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (!title.trim() || !reportedOn) return;
    save.mutate(
      {
        id: damage?.id,
        vehicleId,
        reportedByPersonId: damage ? undefined : personId,
        title: title.trim(),
        description: description.trim() || null,
        severity,
        reportedOn,
        imageURL: damage?.imageURL ?? null,
        imageFile,
      },
      { onSuccess: () => onOpenChange(false) },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>
              {damage ? "Edit damage" : "Report damage"}
            </DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4 sm:grid-cols-2">
            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="damage-title">Title</Label>
              <Input
                id="damage-title"
                value={title}
                onChange={(event) => setTitle(event.target.value)}
                placeholder="e.g. Scratch on rear door"
                data-testid="damage-title-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Severity</Label>
              <Select
                value={severity}
                onValueChange={(value) =>
                  setSeverity(value as VehicleDamageSeverity)
                }
              >
                <SelectTrigger data-testid="damage-severity-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(damageSeverityLabels).map(
                    ([value, label]) => (
                      <SelectItem key={value} value={value}>
                        {label}
                      </SelectItem>
                    ),
                  )}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="damage-date">Reported on</Label>
              <DatePicker
                id="damage-date"
                value={reportedOn}
                onChange={setReportedOn}
              />
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="damage-description">Description</Label>
              <Textarea
                id="damage-description"
                value={description}
                onChange={(event) => setDescription(event.target.value)}
                className="min-h-[100px]"
              />
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="damage-photo">Photo</Label>
              <Input
                id="damage-photo"
                type="file"
                accept="image/*"
                onChange={(event) =>
                  setImageFile(event.target.files?.[0] ?? null)
                }
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!title.trim() || !reportedOn || save.isPending}
              data-testid="save-damage"
            >
              Save
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function DamageCard({
  damage,
  onEdit,
}: {
  damage: VehicleDamage;
  onEdit: () => void;
}) {
  const setStatus = useSetVehicleDamageStatus();
  const remove = useDeleteVehicleDamage();
  const isOpen = damage.status === "open";

  return (
    <Card data-testid={`vehicle-damage-${damage.id}`}>
      <CardContent className="flex gap-4 p-4">
        {damage.imageURL && (
          <a href={damage.imageURL} target="_blank" rel="noreferrer">
            <img
              src={damage.imageURL}
              alt=""
              loading="lazy"
              className="h-16 w-16 max-w-none rounded-md border object-cover sm:h-20 sm:w-20"
            />
          </a>
        )}
        <div className="min-w-0 flex-1 space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <p className="font-medium">{damage.title}</p>
            <Badge className={damageSeverityClasses[damage.severity]}>
              {damageSeverityLabels[damage.severity]}
            </Badge>
          </div>
          <p className="text-sm text-muted-foreground">
            Reported {formatDate(new Date(damage.reportedOn))}
            {damage.reportedBy &&
              ` by ${damage.reportedBy.firstName} ${damage.reportedBy.lastName}`}
            {damage.resolvedOn &&
              ` · resolved ${formatDate(new Date(damage.resolvedOn))}`}
          </p>
          {damage.description && (
            <p className="whitespace-pre-wrap text-sm">{damage.description}</p>
          )}
        </div>
        <div className="flex shrink-0 flex-col gap-1 sm:flex-row sm:items-start">
          <Button
            size="sm"
            variant="outline"
            onClick={() =>
              setStatus.mutate({
                id: damage.id,
                status: isOpen ? "resolved" : "open",
              })
            }
            data-testid={`toggle-damage-status-${damage.id}`}
          >
            {isOpen ? (
              <>
                <CheckCircle2 className="h-4 w-4 mr-1" />
                Mark resolved
              </>
            ) : (
              <>
                <RotateCcw className="h-4 w-4 mr-1" />
                Reopen
              </>
            )}
          </Button>
          <Button
            size="icon"
            variant="ghost"
            onClick={onEdit}
            aria-label="Edit damage"
          >
            <Pencil className="h-4 w-4" />
          </Button>
          <Button
            size="icon"
            variant="ghost"
            onClick={() => {
              if (confirm(`Delete "${damage.title}"?`))
                remove.mutate(damage.id);
            }}
            aria-label="Delete damage"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export function VehicleDamagesTab({
  vehicleId,
  damages,
}: {
  vehicleId: string;
  damages: VehicleDamage[];
}) {
  const [dialog, setDialog] = useState<{
    open: boolean;
    damage: VehicleDamage | null;
  }>({ open: false, damage: null });
  const [showResolved, setShowResolved] = useState(false);

  const open = damages.filter((damage) => damage.status === "open");
  const resolved = damages.filter((damage) => damage.status === "resolved");

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-muted-foreground">
          Open defects & damages ({open.length})
        </h3>
        <Button
          onClick={() => setDialog({ open: true, damage: null })}
          data-testid="add-damage"
        >
          <Plus className="h-4 w-4 mr-2" />
          Report damage
        </Button>
      </div>

      {open.length === 0 ? (
        <div className="rounded-lg border py-8 text-center text-muted-foreground">
          No open defects or damages.
        </div>
      ) : (
        <div className="space-y-2">
          {open.map((damage) => (
            <DamageCard
              key={damage.id}
              damage={damage}
              onEdit={() => setDialog({ open: true, damage })}
            />
          ))}
        </div>
      )}

      {resolved.length > 0 && (
        <Collapsible open={showResolved} onOpenChange={setShowResolved}>
          <CollapsibleTrigger className="flex items-center gap-1 text-sm font-semibold text-muted-foreground hover:text-foreground">
            <ChevronRight
              className={cn(
                "h-4 w-4 transition-transform duration-200",
                showResolved && "rotate-90",
              )}
            />
            Resolved ({resolved.length})
          </CollapsibleTrigger>
          <CollapsibleContent className="mt-2 space-y-2">
            {resolved.map((damage) => (
              <DamageCard
                key={damage.id}
                damage={damage}
                onEdit={() => setDialog({ open: true, damage })}
              />
            ))}
          </CollapsibleContent>
        </Collapsible>
      )}

      <DamageDialog
        open={dialog.open}
        onOpenChange={(value) =>
          setDialog((prev) => ({ ...prev, open: value }))
        }
        vehicleId={vehicleId}
        damage={dialog.damage}
      />
    </div>
  );
}
